import { motion } from "framer-motion";
import {
  Users,
  Heart,
  BookOpen,
  Shield,
  Globe,
  Award,
  Calendar,
  MapPin,
  ArrowRight,
  CheckCircle,
  Star,
  TrendingUp,
  Target,
  Lightbulb,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  animationVariants,
  transitions,
  motionProps,
} from "../hooks/useOptimizedAnimation";

const OurWork = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState("all");
  const [hoveredProject, setHoveredProject] = useState(null);

  const categories = [
    {
      id: "all",
      name: t("ourWork.categories.all"),
      icon: Globe,
      color: "from-blue-500 to-blue-600",
    },
    {
      id: "education",
      name: t("ourWork.categories.education"),
      icon: BookOpen,
      color: "from-green-500 to-green-600",
    },
    {
      id: "healthcare",
      name: t("ourWork.categories.healthcare"),
      icon: Heart,
      color: "from-red-500 to-red-600",
    },
    {
      id: "community",
      name: t("ourWork.categories.community"),
      icon: Users,
      color: "from-purple-500 to-purple-600",
    },
    {
      id: "advocacy",
      name: t("ourWork.categories.advocacy"),
      icon: Shield,
      color: "from-orange-500 to-orange-600",
    },
  ];

  const projects = [
    {
      id: 1,
      title: t("ourWork.projects.digitalLiteracy.title"),
      category: "education",
      description: t("ourWork.projects.digitalLiteracy.description"),
      image:
        "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      stats: {
        participants: 500,
        duration: t("ourWork.projects.digitalLiteracy.duration"),
        location: t("ourWork.projects.digitalLiteracy.location"),
        completion: 100,
      },
      impact: t("ourWork.projects.digitalLiteracy.impact"),
      status: "completed",
      featured: true,
      highlights: [
        t("ourWork.projects.digitalLiteracy.highlights.computer"),
        t("ourWork.projects.digitalLiteracy.highlights.internet"),
        t("ourWork.projects.digitalLiteracy.highlights.job"),
      ],
    },
    {
      id: 2,
      title: t("ourWork.projects.healthcare.title"),
      category: "healthcare",
      description: t("ourWork.projects.healthcare.description"),
      image: "/images/health-care.jpg",
      stats: {
        participants: 1200,
        duration: t("ourWork.projects.healthcare.duration"),
        location: t("ourWork.projects.healthcare.location"),
        completion: 75,
      },
      impact: t("ourWork.projects.healthcare.impact"),
      status: "ongoing",
      featured: true,
      highlights: [
        t("ourWork.projects.healthcare.highlights.mobile"),
        t("ourWork.projects.healthcare.highlights.education"),
        t("ourWork.projects.healthcare.highlights.supplies"),
      ],
    },
    {
      id: 3,
      title: t("ourWork.projects.youthLeadership.title"),
      category: "community",
      description: t("ourWork.projects.youthLeadership.description"),
      image:
        "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      stats: {
        participants: 150,
        duration: t("ourWork.projects.youthLeadership.duration"),
        location: t("ourWork.projects.youthLeadership.location"),
        completion: 100,
      },
      impact: t("ourWork.projects.youthLeadership.impact"),
      status: "completed",
      featured: false,
      highlights: [
        t("ourWork.projects.youthLeadership.highlights.leadership"),
        t("ourWork.projects.youthLeadership.highlights.civic"),
        t("ourWork.projects.youthLeadership.highlights.mentorship"),
      ],
    },
    {
      id: 4,
      title: t("ourWork.projects.womenEmpowerment.title"),
      category: "community",
      description: t("ourWork.projects.womenEmpowerment.description"),
      image: "/images/women.jpg",
      stats: {
        participants: 300,
        duration: t("ourWork.projects.womenEmpowerment.duration"),
        location: t("ourWork.projects.womenEmpowerment.location"),
        completion: 80,
      },
      impact: t("ourWork.projects.womenEmpowerment.impact"),
      status: "ongoing",
      featured: false,
      highlights: [
        t("ourWork.projects.womenEmpowerment.highlights.skills"),
        t("ourWork.projects.womenEmpowerment.highlights.microfinance"),
        t("ourWork.projects.womenEmpowerment.highlights.business"),
      ],
    },
    {
      id: 5,
      title: t("ourWork.projects.humanRights.title"),
      category: "advocacy",
      description: t("ourWork.projects.humanRights.description"),
      image: "/images/IMG-20250814-WA0009.jpg",
      stats: {
        participants: 800,
        duration: t("ourWork.projects.humanRights.duration"),
        location: t("ourWork.projects.humanRights.location"),
        completion: 65,
      },
      impact: t("ourWork.projects.humanRights.impact"),
      status: "ongoing",
      featured: false,
      highlights: [
        t("ourWork.projects.humanRights.highlights.education"),
        t("ourWork.projects.humanRights.highlights.legal"),
        t("ourWork.projects.humanRights.highlights.campaigns"),
      ],
    },
    {
      id: 6,
      title: t("ourWork.projects.emergencyResponse.title"),
      category: "healthcare",
      description: t("ourWork.projects.emergencyResponse.description"),
      image: "/images/IMG-20250814-WA0001.jpg",
      stats: {
        participants: 2000,
        duration: t("ourWork.projects.emergencyResponse.duration"),
        location: t("ourWork.projects.emergencyResponse.location"),
        completion: 90,
      },
      impact: t("ourWork.projects.emergencyResponse.impact"),
      status: "ongoing",
      featured: false,
      highlights: [
        t("ourWork.projects.emergencyResponse.highlights.response"),
        t("ourWork.projects.emergencyResponse.highlights.relief"),
        t("ourWork.projects.emergencyResponse.highlights.support"),
      ],
    },
  ];

  const achievements = [
    {
      number: "5,000+",
      label: t("ourWork.stats.beneficiaries"),
      icon: Users,
      description: t("ourWork.stats.beneficiariesDesc"),
      color: "from-blue-500 to-blue-600",
    },
    {
      number: "15",
      label: t("ourWork.stats.projects"),
      icon: Award,
      description: t("ourWork.stats.projectsDesc"),
      color: "from-green-500 to-green-600",
    },
    {
      number: "10",
      label: t("ourWork.stats.impact"),
      icon: Calendar,
      description: t("ourWork.stats.impactDesc"),
      color: "from-purple-500 to-purple-600",
    },
    {
      number: "25+",
      label: t("ourWork.stats.communities"),
      icon: MapPin,
      description: t("ourWork.stats.communitiesDesc"),
      color: "from-orange-500 to-orange-600",
    },
  ];

  const impactMetrics = [
    {
      icon: TrendingUp,
      title: t("ourWork.approach.sustainable.title"),
      description: t("ourWork.approach.sustainable.description"),
      color: "text-green-600",
    },
    {
      icon: Target,
      title: t("ourWork.approach.focused.title"),
      description: t("ourWork.approach.focused.description"),
      color: "text-blue-600",
    },
    {
      icon: Lightbulb,
      title: t("ourWork.approach.innovation.title"),
      description: t("ourWork.approach.innovation.description"),
      color: "text-purple-600",
    },
    {
      icon: Zap,
      title: t("ourWork.approach.rapid.title"),
      description: t("ourWork.approach.rapid.description"),
      color: "text-orange-600",
    },
  ];

  const filteredProjects =
    activeCategory === "all"
      ? projects
      : projects.filter((project) => project.category === activeCategory);

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <div
        className=" left-0 right-0 top-0 bottom-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/images/IMG-20250814-WA0005.jpg')",
        }}
      >
        <div className="relative py-32 px- lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="text-center"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-8"
              >
                <Globe className="h-5 w-5 text-[rgb(210,166,73)]" />
                <span className="text-white font-medium">
                  {t("ourWork.subtitle")}
                </span>
              </motion.div>

              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                {t("ourWork.title")}
              </h1>
              <p className="text-xl md:text-2xl text-white/90 max-w-4xl mx-auto mb-12 leading-relaxed">
                {t("ourWork.description")}
              </p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <button className="bg-[rgb(210,166,73)] text-[rgb(4,50,75)] px-8 py-4 rounded-full font-semibold hover:bg-[rgb(246,203,110)] transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2">
                  <span>{t("ourWork.cta1")}</span>
                  <ArrowRight className="h-5 w-5" />
                </button>
                <button className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-[rgb(4,50,75)] transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2">
                  <span>{t("ourWork.cta2")}</span>
                  <Heart className="h-5 w-5" />
                </button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Achievement Stats */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-[rgb(4,50,75)] mb-4">
              {t("ourWork.impactNumbers.title")}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("ourWork.impactNumbers.subtitle")}
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.label}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
                className="text-center group"
              >
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r ${achievement.color} mb-6 group-hover:scale-110 transition-transform duration-300`}
                >
                  <achievement.icon className="h-10 w-10 text-white" />
                </div>
                <div className="text-4xl font-bold text-[rgb(4,50,75)] mb-2">
                  {achievement.number}
                </div>
                <div className="text-lg font-semibold text-gray-800 mb-2">
                  {achievement.label}
                </div>
                <div className="text-sm text-gray-600">
                  {achievement.description}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Approach */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[rgb(4,50,75)] to-[rgb(29,84,114)]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              {t("ourWork.approach.title")}
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              {t("ourWork.approach.subtitle")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {impactMetrics.map((metric, index) => (
              <motion.div
                key={metric.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
                className="text-center group"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm mb-6 group-hover:scale-110 transition-transform duration-300">
                  <metric.icon className={`h-8 w-8 ${metric.color}`} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {metric.title}
                </h3>
                <p className="text-white/80">{metric.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-[rgb(4,50,75)] mb-4">
              {t("ourWork.featured.title")}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("ourWork.featured.subtitle")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            {filteredProjects
              .filter((project) => project.featured)
              .map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
                  className="group"
                >
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                    <div className="relative h-80">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                      <div className="absolute top-6 left-6 flex space-x-3">
                        <span
                          className={`px-4 py-2 rounded-full text-sm font-medium ${
                            project.status === "completed"
                              ? "bg-green-100 text-green-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {project.status === "completed"
                            ? t("ourWork.status.completed")
                            : t("ourWork.status.ongoing")}
                        </span>
                        {project.featured && (
                          <span className="px-4 py-2 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 flex items-center space-x-1">
                            <Star className="h-4 w-4 fill-current" />
                            <span>{t("ourWork.featured.badge")}</span>
                          </span>
                        )}
                      </div>

                      <div className="absolute bottom-6 left-6 right-6">
                        <h3 className="text-2xl font-bold text-white mb-2">
                          {project.title}
                        </h3>
                        <p className="text-white/90 text-sm">
                          {project.description}
                        </p>
                      </div>
                    </div>

                    <div className="p-8">
                      <div className="grid grid-cols-2 gap-6 mb-6">
                        <div className="text-center p-4 bg-gray-50 rounded-xl">
                          <div className="text-2xl font-bold text-[rgb(4,50,75)]">
                            {project.stats.participants.toLocaleString()}
                          </div>
                          <div className="text-sm text-gray-600">
                            {t("ourWork.participants")}
                          </div>
                        </div>
                        <div className="text-center p-4 bg-gray-50 rounded-xl">
                          <div className="text-2xl font-bold text-[rgb(4,50,75)]">
                            {project.stats.completion}%
                          </div>
                          <div className="text-sm text-gray-600">
                            {t("ourWork.complete")}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-6">
                        {project.highlights.map((highlight, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-[rgb(210,166,73)]/10 text-[rgb(210,166,73)] rounded-full text-sm font-medium"
                          >
                            {highlight}
                          </span>
                        ))}
                      </div>

                      <div className="flex items-center space-x-2 text-sm text-green-600 mb-6">
                        <CheckCircle className="h-5 w-5" />
                        <span className="font-medium">{project.impact}</span>
                      </div>

                      <button className="w-full bg-gradient-to-r from-[rgb(4,50,75)] to-[rgb(29,84,114)] text-white py-4 px-6 rounded-xl hover:from-[rgb(29,84,114)] hover:to-[rgb(4,50,75)] transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 font-semibold">
                        <span>{t("ourWork.learnMore")}</span>
                        <ArrowRight className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
          </div>
        </div>
      </section>

      {/* All Projects Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-[rgb(4,50,75)] mb-4">
              {t("ourWork.allProjects.title")}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t("ourWork.allProjects.subtitle")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
                className="group"
                onMouseEnter={() => setHoveredProject(project.id)}
                onMouseLeave={() => setHoveredProject(null)}
              >
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                  <div className="relative h-64">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    <div className="absolute top-4 left-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          project.status === "completed"
                            ? "bg-green-100 text-green-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {project.status === "completed"
                          ? "Completed"
                          : "Ongoing"}
                      </span>
                    </div>

                    <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <h3 className="text-lg font-bold text-white mb-1">
                        {project.title}
                      </h3>
                      <p className="text-white/90 text-sm line-clamp-2">
                        {project.description}
                      </p>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[rgb(4,50,75)] mb-3 group-hover:hidden">
                      {project.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 group-hover:hidden line-clamp-2">
                      {project.description}
                    </p>

                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <span className="font-medium">
                        {project.stats.participants.toLocaleString()}{" "}
                        {t("ourWork.participants")}
                      </span>
                      <span className="text-[rgb(210,166,73)] font-medium">
                        {project.stats.location}
                      </span>
                    </div>

                    <div className="flex items-center space-x-2 text-sm text-green-600 mb-4">
                      <CheckCircle className="h-4 w-4" />
                      <span className="font-medium">{project.impact}</span>
                    </div>

                    <button className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-xl hover:bg-[rgb(210,166,73)] hover:text-[rgb(4,50,75)] transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2 font-medium">
                      <span>{t("ourWork.viewDetails")}</span>
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[rgb(4,50,75)] to-[rgb(29,84,114)] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('/images/IMG-20250814-WA0001.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="relative max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t("ourWork.cta.title")}
            </h2>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
              {t("ourWork.cta.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button className="bg-[rgb(210,166,73)] text-[rgb(4,50,75)] px-10 py-4 rounded-full font-semibold hover:bg-[rgb(246,203,110)] transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2">
                <span>{t("ourWork.cta.getInvolved")}</span>
                <ArrowRight className="h-5 w-5" />
              </button>
              <button className="border-2 border-white text-white px-10 py-4 rounded-full font-semibold hover:bg-white hover:text-[rgb(4,50,75)] transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2">
                <span>{t("ourWork.cta.donate")}</span>
                <Heart className="h-5 w-5" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OurWork;
